// components/ProjectTypeSelector.jsx
import React, { useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { services } from '../../../data/services';

const ProjectTypeSelector = ({
  onSelect,
  selectedType,
  showMobileSelector,
  setShowMobileSelector,
}) => {
  useEffect(() => {
    if (showMobileSelector) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showMobileSelector]);

  return (
    <div>
      <div className="text-center mb-8 sm:mb-12">
        <h2 className="text-2xl sm:text-3xl font-bold text-primary mb-2 sm:mb-4">
          What Type of Project Are You Planning?
        </h2>
        <p className="text-sm sm:text-lg text-text-secondary max-w-2xl mx-auto">
          Select the service that best matches your vision.
        </p>
      </div>

      <div className="hidden md:grid grid-cols-2 gap-4 sm:gap-6">
        {services.map((type) => (
          <div
            key={type.id}
            onClick={() => onSelect(type)}
            className={`relative p-5 sm:p-8 border-2 rounded-xl cursor-pointer transition-all hover:-translate-y-1
              ${selectedType?.id === type.id
                ? 'border-accent bg-accent/5'
                : 'border-border hover:border-accent/50 hover:bg-surface'
              }`}
          >
            <div className="flex items-start space-x-4 mb-6">
              <div
                className={`w-12 h-12 rounded-lg hidden sm:flex items-center justify-center
                  ${selectedType?.id === type.id
                    ? 'bg-accent text-white'
                    : 'bg-surface text-accent'
                  }`}
              >
                <Icon name={type.iconName} size={24} strokeWidth={2} />
              </div>

              <div className="flex-1">
                <h3 className="text-xl font-bold text-primary mb-2">
                  {type.title}
                </h3>
                <p className="text-text-secondary leading-relaxed">
                  {type.shortdescription}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-primary mb-2">
                  Key Features:
                </h4>
                <ul className="space-y-1">
                  {type.features.map((feature, index) => (
  <li key={index} className="flex items-center space-x-2 text-sm text-text-secondary">
    <Icon name="Check" size={16} className="text-accent" />
    <span>{feature.title}</span>
  </li>
))}

                </ul>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-border">
                <div className="flex items-center space-x-2 text-sm text-text-secondary">
                  <Icon name="Clock" size={16} />
                  <span>{type.timeline}</span>
                </div>
                <div className="text-sm font-semibold text-accent">
                  {type.priceFrom}
                </div>
              </div>
            </div>

            {selectedType?.id === type.id && (
              <div className="absolute top-4 right-4">
                <div className="w-6 h-6 bg-accent rounded-full flex items-center justify-center">
                  <Icon name="Check" size={16} className="text-white" />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="md:hidden">
        <button
          aria-haspopup="dialog"
          onClick={() => setShowMobileSelector(true)}
          className="w-full p-4 border-2 border-dashed border-accent rounded-xl text-left"
        >
          <p className="text-sm text-text-secondary">Selected project type</p>
          <p className="font-semibold text-primary">
            {selectedType ? selectedType.title : 'Tap to choose'}
          </p>
        </button>
      </div>

      <div className="mt-4 sm:mt-12 p-4 sm:p-6 bg-surface rounded-xl">
        <div className="flex items-start space-x-4">
          <div className="w-10 h-10 bg-accent/10 rounded-lg hidden sm:flex items-center justify-center">
            <Icon name="Lightbulb" size={20} className="text-accent" />
          </div>
          <div>
            <h4 className="font-semibold text-primary mb-2">
              Not Sure Which Service You Need?
            </h4>
            <p className="text-text-secondary text-sm leading-relaxed">
              Our consultation process is designed to help identify the perfect approach.
            </p>
          </div>
        </div>
      </div>

      <div
        onClick={() => setShowMobileSelector(false)}
        className={`
          fixed inset-0 z-[9997] bg-black/40 backdrop-blur-sm
          transition-opacity timeline-300
          md:hidden
          ${showMobileSelector ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
      />

      <aside
        role="dialog"
        aria-modal="true"
        className={`
          fixed z-[9998] left-0 right-0 bottom-0
          bg-white border-t border-border
          rounded-t-3xl
          transform transition-all timeline-500
          md:hidden
          ${showMobileSelector
            ? 'translate-y-0 opacity-100'
            : 'translate-y-full opacity-0 pointer-events-none'}
        `}
      >
        <div className="p-4 sm:p-6">
          <div className="flex justify-center mb-3">
            <div className="h-1.5 w-12 rounded-full bg-gray-300" />
          </div>

          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-primary">
              Select Project Type
            </h3>
            <button
              onClick={() => setShowMobileSelector(false)}
              className="text-sm px-3 py-1 rounded-md text-gray-600 hover:bg-surface"
            >
              Close
            </button>
          </div>

          <div className="space-y-2 max-h-[60vh] overflow-y-auto overscroll-contain pr-1">
            {services.map((type) => (
              <button
                key={type.id}
                onClick={() => {
                  onSelect(type);
                  setShowMobileSelector(false);
                }}
                className={`
                  w-full flex items-start gap-3 p-4 rounded-xl text-left transition-all active:scale-[0.98]
                  ${selectedType?.id === type.id
                    ? 'bg-accent text-white'
                    : 'bg-surface hover:bg-accent/10'}
                `}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-white/20">
                  <Icon name={type.icon} size={20} />
                </div>

                <div className="flex-1">
                  <p className="font-semibold">{type.title}</p>
                  <p
                    className={`text-sm ${selectedType?.id === type.id
                      ? 'text-white/80'
                      : 'text-text-secondary'
                      }`}
                  >
                    {type.timeline} • {type.investment}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
};

export default ProjectTypeSelector;